import { useEffect, useState } from "react";
import { FileText, Pencil, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { act, useFetch } from "@/lib/hooks";
import { fmtDate } from "@/lib/format";
import { Field, Loading, PageHeader, Panel, Table, cellCls } from "@/components/common";
import { selectCls } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function UserDialog({ user, roles, open, onOpenChange, onSaved }: any) {
  const [f, setF] = useState<any>({});
  useEffect(() => {
    if (open) setF(user ? { ...user, password: "" } : { login: "", name: "", role: "mecanicien", password: "", active: true });
  }, [open, user]);
  const save = async () => {
    const body = { ...f, password: f.password || null };
    if (await act(() => (user ? api.put(`/users/${user.id}`, body) : api.post("/users", body)), "Utilisateur enregistré")) {
      onSaved();
      onOpenChange(false);
    }
  };
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{user ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
        <Field label="Identifiant de connexion"><Input value={f.login || ""} onChange={set("login")} disabled={!!user} placeholder="karim ou karim@nms.dz" data-testid="user-login-input" /></Field>
        <Field label="Nom affiché"><Input value={f.name || ""} onChange={set("name")} data-testid="user-name-input" /></Field>
        <Field label="Rôle">
          <select className={selectCls} value={f.role} onChange={set("role")} data-testid="user-role-select">
            {Object.entries(roles).map(([k, l]: any) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label={user ? "Nouveau mot de passe (laisser vide pour garder)" : "Mot de passe (8 caractères min.)"}><PasswordInput value={f.password || ""} onChange={set("password")} autoComplete="new-password" data-testid="user-password-input" toggleTestId="user-password-toggle" /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={set("active")} data-testid="user-active-checkbox" />Compte actif</label>
        <Button className="h-11" onClick={save} data-testid="user-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { data, reload } = useFetch<any>("/users");
  const [edit, setEdit] = useState<any>(undefined);
  if (!data) return <Loading />;
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Utilisateurs & rôles" actions={<Button className="h-11" onClick={() => setEdit(null)} data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" />Utilisateur</Button>} />
      <Panel>
        <Table head={["Nom", "Identifiant", "Rôle", "Statut", "Dernière connexion", ""]} testId="users-table">
          {data.users.map((u: any) => (
            <tr key={u.id} className="border-b border-border/60" data-testid={`user-row-${u.login}`}>
              <td className={`${cellCls} font-semibold`}>{u.name}</td>
              <td className={`${cellCls} font-mono-data text-xs`}>{u.login}</td>
              <td className={cellCls}>{data.roles[u.role]}</td>
              <td className={cellCls}>{u.active ? <span className="text-emerald-400">Actif</span> : <span className="text-muted-foreground">Désactivé</span>}</td>
              <td className={`${cellCls} text-muted-foreground`}>{fmtDate(u.last_login)}</td>
              <td className={`${cellCls} text-right`}><Button size="sm" variant="ghost" onClick={() => setEdit(u)} data-testid={`user-edit-${u.login}`}><Pencil className="h-4 w-4" /></Button></td>
            </tr>
          ))}
        </Table>
      </Panel>
      <Panel title="Fiches de poste à afficher (PDF, 1 page)" className="mt-6" testId="role-sheets-panel">
        <div className="flex flex-wrap gap-3">
          {[["mecanicien", "Mécanicien"], ["caissier", "Caissier"], ["reception", "Réception"]].map(([k, l]) => (
            <a key={k} href={`/fiche-${k}.pdf`} target="_blank" rel="noreferrer" data-testid={`role-sheet-${k}`}>
              <Button variant="outline" className="h-11"><FileText className="mr-2 h-4 w-4" />Fiche {l}</Button>
            </a>
          ))}
        </div>
      </Panel>
      <UserDialog user={edit} roles={data.roles} open={edit !== undefined} onOpenChange={(o: boolean) => !o && setEdit(undefined)} onSaved={reload} />
    </div>
  );
}
